import React, { Children, cloneElement, useEffect, useMemo, useState } from 'react';

const DEFAULT_PAGE_SIZE = 10;

const AdminTablePagination = ({ children, pageSize = DEFAULT_PAGE_SIZE }) => {
  const [page, setPage] = useState(1);
  const wrapper = Children.only(children);
  const table = Children.only(wrapper.props.children);
  const tableChildren = Children.toArray(table.props.children);
  const body = tableChildren.find((child) => child.type === 'tbody');
  const rows = body ? Children.toArray(body.props.children) : [];

  const rowSignature = useMemo(
    () => rows.map((row) => row.key || '').join('|'),
    [rows]
  );
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));

  useEffect(() => {
    setPage(1);
  }, [rowSignature]);

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, pageCount));
  }, [pageCount]);

  if (!body) return children;

  const startIndex = (page - 1) * pageSize;
  const visibleRows = rows.slice(startIndex, startIndex + pageSize);
  const paginatedBody = cloneElement(body, {}, visibleRows);
  const paginatedTable = cloneElement(
    table,
    {},
    tableChildren.map((child) => child === body ? paginatedBody : child)
  );
  const paginatedWrapper = cloneElement(wrapper, {}, paginatedTable);

  if (rows.length <= pageSize) return paginatedWrapper;

  const firstVisibleRow = startIndex + 1;
  const lastVisibleRow = Math.min(startIndex + pageSize, rows.length);

  return (
    <div className="space-y-3">
      {paginatedWrapper}
      <div className="flex flex-col items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white px-5 py-4 sm:flex-row">
        <p className="text-xs font-bold text-slate-500">
          Showing {firstVisibleRow}–{lastVisibleRow} of {rows.length}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
            disabled={page === 1}
            className="rounded-xl border border-gray-200 px-4 py-2 text-xs font-black text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>
          <span className="min-w-20 text-center text-xs font-black text-slate-500">
            Page {page} of {pageCount}
          </span>
          <button
            type="button"
            onClick={() => setPage((currentPage) => Math.min(pageCount, currentPage + 1))}
            disabled={page === pageCount}
            className="rounded-xl border border-gray-200 px-4 py-2 text-xs font-black text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminTablePagination;
